import { useCallback, useEffect, useRef, useState } from "react";

import { getCursorPath } from "../../../services";
import { fetchWithAuth } from "../../../api-client/APIClient";
import { HTTPMethod } from "@merge-api/merge-javascript-shared";
import { showErrorToast } from "../Toasts";

type UsePaginatedRequestProps = {
  rootPath: string;
  paramsPath?: string;
  errorText?: string;
  skip?: boolean;
};

/**
 * Hook for handling paginated request results. Loads initial value and provideds
 * next and previous values
 */
export function usePaginatedRequest<T>({
  rootPath,
  paramsPath = "",
  errorText,
  skip = false,
}: UsePaginatedRequestProps) {
  // hooks
  const [previousURL, setPreviousURL] = useState<string>();
  const [nextURL, setNextURL] = useState<string>();
  const [results, setResults] = useState<T[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [errorStatus, setErrorStatus] = useState<number>();
  const [hasInitialized, setHasInitialized] = useState(false);
  const currentPath = useRef("");
  const currentQuery = useRef("");
  const hasMounted = useRef(false);

  // event handlers
  const fetchWithCursor = useCallback(
    (cursor = "") => {
      setIsLoading(true);
      setErrorStatus(undefined);

      const query = `${rootPath}?${paramsPath}`;
      if (query !== currentQuery.current) {
        setNextURL(undefined);
        setPreviousURL(undefined);
        currentQuery.current = query;
        cursor = "";
      }

      const cursorPath = getCursorPath(cursor) ? `${getCursorPath(cursor)}&` : "";
      const path = `${rootPath}?${cursorPath}${paramsPath}`;

      currentPath.current = path;

      fetchWithAuth({
        path,
        method: HTTPMethod.GET,
        onResponse: (data: { next: string; previous: string; results: T[] }) => {
          if (path !== currentPath.current) return; // prevent race condition

          setNextURL(data.next);
          setPreviousURL(data.previous);
          setResults(data.results);
          setIsLoading(false);
        },
        onError: (response) => {
          if (path !== currentPath.current) return;
          setErrorStatus(response?.status ?? 0);
          errorText && showErrorToast(errorText);
          console.error(response);
          setResults([]);
          setIsLoading(false);
        },
      });
    },
    [errorText, paramsPath, rootPath],
  );

  const onPrevious = () => fetchWithCursor(previousURL);
  const onNext = () => fetchWithCursor(nextURL);

  /**
   * Allows removing an item from data w/o having to refetch. Used when deleting an item.
   */
  const onRemove = (index: number) => {
    if (!results) return;
    setResults([...results.slice(0, index), ...results.slice(index + 1)]);
  };

  /**
   * Allows replacing an item in data w/o having to refetch. Used when an item is edited in place.
   */
  const onReplace = (matches: (item: T) => boolean, replace: (item: T) => T) => {
    setResults((current) => current?.map((item) => (matches(item) ? replace(item) : item)));
  };

  // effects
  // initialize. `skip` gates only this automatic fetch, so callers can still refetch explicitly.
  useEffect(() => {
    if (!hasInitialized && !skip) {
      fetchWithCursor();
      setHasInitialized(true);
    }
  }, [fetchWithCursor, hasInitialized, skip]);

  // refetch on params path change. Must skip the first render: it runs in the same commit as the
  // init effect above, so resetting here would undo its setHasInitialized(true), and since the net
  // value never changes nothing re-runs to set it true again (hasInitialized stayed false forever).
  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }
    setHasInitialized(false);
  }, [paramsPath]);

  return {
    results,
    isLoading,
    errorStatus,
    onPrevious,
    onNext,
    onRemove,
    onReplace,
    refetch: fetchWithCursor,
    hasPrevious: !!previousURL,
    hasNext: !!nextURL,
  };
}
